/**
 * Maps backend activity data to ActivityListWidget view model.
 * Isolated so backend contract changes don't require rewriting UI (WID-09).
 */
import { ACTIVITY_TYPE_CONFIG, ALERT_TYPES } from '../constants';
import { formatTimeAgo } from '../../../../utils/dateFormatters';

const DEFAULT_TYPE = 'call';

/**
 * The contract's `type` is upper snake (`CALL`, `EMAIL`); the icon config is
 * keyed lowercase. Folding the case here rather than re-keying the config
 * keeps every caller that already passes `call` working.
 */
const typeKeyOf = (raw) => {
  const key = String(raw ?? '').toLowerCase();
  return ACTIVITY_TYPE_CONFIG[key] ? key : DEFAULT_TYPE;
};

export const mapActivityItem = (raw) => {
  const type = typeKeyOf(raw?.type);
  return {
    id: raw?.id ?? raw?.row_key ?? '',
    type,
    title: raw?.title ?? raw?.description ?? 'Title of the activity',
    description: raw?.description ?? raw?.content ?? '',
    author: raw?.author ?? raw?.userName ?? raw?.actor ?? '',
    timestamp: raw?.timestamp ?? raw?.date ?? raw?.createdAt ?? raw?.occurred_at ?? '',
    timeAgo: raw?.timeAgo ?? formatTimeAgo(raw?.timestamp ?? raw?.date ?? raw?.occurred_at),
    alerts: normalizeAlerts(raw?.alerts ?? raw?.criticalState),
    metadata: {
      userId: raw?.userId ?? raw?.metadata?.userId ?? '',
      sentiment: raw?.sentiment ?? raw?.metadata?.sentiment,
      assignedTasks: raw?.assignedTasks ?? raw?.metadata?.assignedTasks ?? 0,
      hasRecording: raw?.hasRecording ?? raw?.metadata?.hasRecording ?? false,
      hasExternalLink: raw?.hasExternalLink ?? raw?.metadata?.hasExternalLink ?? false,
      direction: raw?.direction ?? raw?.metadata?.direction,
      duration: raw?.duration ?? raw?.metadata?.duration ?? '',
      flagged: raw?.flagged ?? raw?.metadata?.flagged ?? false,
      commentsCount: raw?.commentsCount ?? raw?.metadata?.commentsCount ?? 0,
      attachmentsCount: raw?.attachmentsCount ?? raw?.metadata?.attachmentsCount ?? 0,
      linkCount: raw?.linkCount ?? raw?.metadata?.linkCount ?? 0,
    },
  };
};

/**
 * `ACTIVITY` (contract v2) publishes `events[]`, each
 * `{row_key, occurred_at, title, description, actor, type}`.
 *
 * It is read as the same list `items[]` always was — the keys differ, the
 * thing does not — so the drawing, the compressed variant and the alert rules
 * stay exactly as they are. A payload that still sends `items[]` keeps
 * working, which is what the widget's existing callers send today.
 */
export const mapActivityListData = (rawData) => {
  if (!rawData) return { title: '', items: [] };
  const raw = Array.isArray(rawData.events)
    ? rawData.events
    : (Array.isArray(rawData.items) ? rawData.items : []);
  return {
    title: rawData.title ?? 'Activity',
    items: raw.map(mapActivityItem),
  };
};

function normalizeAlerts(alerts) {
  if (!alerts) return [];
  const arr = Array.isArray(alerts) ? alerts : [alerts];
  return arr
    .map((a) => {
      if (typeof a === 'string') {
        if (a.toLowerCase().includes('expired')) return { type: ALERT_TYPES.EXPIRED, message: a };
        return { type: ALERT_TYPES.NEEDS_ATTENTION, message: a };
      }
      return {
        type: a.type === 'expired' ? ALERT_TYPES.EXPIRED : ALERT_TYPES.NEEDS_ATTENTION,
        message: a.message ?? a.description ?? '',
      };
    })
    .filter((a) => a.message);
}
